import { useState } from 'react';
import axios from 'axios';
import { uuidv4 } from 'common/uuidGenerator';
import { useToast } from 'context/ToastContext';
import { toastConstant } from 'constants/toastmessage';

const useDownloadProgress = () => {
  const [downloads, setDownloads] = useState([]);
  const { addToast } = useToast();

  const downloadFile = (url, fileName, onComplete, onProgress, addToGlobalList = true) => {
    if (!url) {
      return;
    }

    const id = uuidv4();
    const download = {
      id,
      progress: 0,
      showProgressBar: true,
      fileName,
      error: null,
    };

    if (addToGlobalList) {
      setDownloads((prevDownloads) => [...prevDownloads, download]);
    }

    axios({
      url: url,
      method: 'GET',
      responseType: 'blob',
      excludeFromLoading: true,
      onDownloadProgress: (progressEvent) => {
        const totalLength = progressEvent.lengthComputable
          ? progressEvent.total
          : progressEvent.target.getResponseHeader('content-length') ||
            progressEvent.target.getResponseHeader('x-decompressed-content-length');
        if (totalLength) {
          const progress = Math.round((progressEvent.loaded * 100) / totalLength);

          setDownloads((prevDownloads) => prevDownloads.map((download) => (download.id === id ? { ...download, progress } : download)));

          // Call the progress callback if provided
          if (onProgress && typeof onProgress === 'function') {
            onProgress(progress);
          }
        }
      },
    })
      .then((response) => {
        const mediaType = response?.headers['content-type'];
        const downloadUrl = window.URL.createObjectURL(new Blob([response.data], { type: mediaType }));

        const link = document.createElement('a');
        link.href = downloadUrl;
        link.setAttribute('download', fileName || 'no-name');
        document.body.appendChild(link);
        link.click();

        if (addToGlobalList) {
          setDownloads((prevDownloads) => prevDownloads.filter((download) => download.id !== id));
        }

        // Call the completion callback if provided
        if (onComplete && typeof onComplete === 'function') {
          onComplete();
        }
      })
      .catch((error) => {
        console.error('Error downloading file:', error);

        addToast(false, toastConstant.toasterType.ERROR, toastConstant.api.FAILED, error?.response?.data);

        if (addToGlobalList) {
          setDownloads((prevDownloads) =>
            prevDownloads.map((download) => (download.id === id ? { ...download, showProgressBar: true, error } : download))
          );
        }

        // Call the completion callback even on error if provided
        if (onComplete && typeof onComplete === 'function') {
          onComplete();
        }
      });
  };

  const remove = (id) => {
    setDownloads((prevDownloads) => prevDownloads.filter((download) => download.id !== id));
  };

  return { downloads, downloadFile, remove };
};

export default useDownloadProgress;
